import React, { useState, useCallback, useMemo } from "react";
import { supabase } from "../../lib/supabase";
import { useData } from "../../hooks/useData";
import { useAuth } from "../../context/AuthContext";
import { useRBAC } from "../../hooks/useRBAC";
import { useSetterPerformance } from "../../hooks/useSetterPerformance";
import { useOrgSetterIds } from "../../hooks/useOrgSetterIds";
import { useDismissedLeaderboard } from "../../hooks/useDismissedLeaderboard";
import { SetterScorecardGrid } from "./SetterScorecardGrid";
import { SetterProofModal } from "./SetterProofModal";
import { ConversationViewModal } from "./ConversationViewModal";
import { DismissedLeadsPanel } from "./DismissedLeadsPanel";
import { DismissedLeadsGridModal } from "./DismissedLeadsGridModal";
import type { ProofMetricType } from "../../types/core";
import type { NavSibling } from "../../hooks/data-fetching/useConversationPreviews";

// =============================================================================
// SETTER PERFORMANCE TAB
// =============================================================================
// Container component for the Setter Performance tab on DM Analytics page.
// Composes: SetterScorecardGrid + SetterProofModal + ConversationViewModal
//
// Features:
// - Individual scorecard per setter (1=centered, 2=side-by-side, 3+=tabs)
// - Click any metric → proof modal with bucket summary + conversation list
// - Click any conversation in proof → full ConversationViewModal
// - Uses global date range from parent
// =============================================================================

interface SetterPerformanceTabProps {
  dateRange: { start: Date | null; end: Date | null };
}

const SetterPerformanceTab: React.FC<SetterPerformanceTabProps> = ({ dateRange }) => {
  const { currentOrganization } = useData();
  const { user } = useAuth();
  const { hasTunnelVision, isSetter } = useRBAC();
  const [selectedConversation, setSelectedConversation] = useState<{
    id: string;
    contactName: string;
    leadInstagram: string | null;
    highlightTimestamp?: string;
    anchorTimestamp?: string;
    // dm-swipe-nav (P1) — set only when opened from the proof grid.
    siblings?: NavSibling[];
    index?: number;
    hasMore?: boolean;
  } | null>(null);

  // Proof modal state — tracks which metric AND which setter was clicked
  const [proofMetric, setProofMetric] = useState<ProofMetricType | null>(null);
  const [proofSetterId, setProofSetterId] = useState<string | null>(null);
  const [proofDefaultWindowMode, setProofDefaultWindowMode] = useState<"lifetime" | "window">(
    "lifetime"
  );

  // Dismissed Leads leaderboard — org-wide "who dismisses the most" (both cuts) +
  // true total (server-side aggregate, not the capped 1000-row list count).
  const leaderboard = useDismissedLeaderboard({
    organizationId: currentOrganization?.id || null,
    dateRange,
    timezone: currentOrganization?.timezone || "UTC",
  });
  // Stable ref for the dismiss-path callbacks — keeps the useCallback dep
  // specific (refreshLeaderboard), so React Compiler's inferred dep matches.
  const refreshLeaderboard = leaderboard.refresh;

  // 3-col grid modal for the dismissed pile — open state lives here so the tab
  // can render it alongside the sibling proof/conversation modals.
  const [gridOpen, setGridOpen] = useState(false);

  // Calculate day count for per-day metrics
  const dayCount = useMemo(() => {
    if (!dateRange.start || !dateRange.end) return 1;
    const diffMs = dateRange.end.getTime() - dateRange.start.getTime();
    return Math.max(Math.ceil(diffMs / (1000 * 60 * 60 * 24)), 1);
  }, [dateRange.start, dateRange.end]);

  // Dynamically resolve setter IDs from organization_members → profiles.ghl_user_id
  const { setterIds: allSetterIds, isLoading: isLoadingSetters } = useOrgSetterIds(
    currentOrganization?.id || null
  );

  // Setters see ALL setters in their org (full transparency, Mako 2026-05-03).
  // Closers/dialers retain tunnel vision — narrowed to their own ghl_user_id
  // (which won't match any setter, so they get an empty grid as before).
  const ghlUserId = user?.ghlUserId;
  const setterIds = useMemo(() => {
    if (!hasTunnelVision || isSetter || !ghlUserId) return allSetterIds;
    return allSetterIds.filter((id) => id === ghlUserId);
  }, [hasTunnelVision, isSetter, ghlUserId, allSetterIds]);

  // Fetch setter performance data
  const {
    metrics: rawMetrics,
    isLoading,
    error,
    refresh,
  } = useSetterPerformance({
    organizationId: currentOrganization?.id || null,
    setterIds,
    dateRange,
    timezone: currentOrganization?.timezone || "UTC",
  });

  // Stable alphabetical sort by setter_name. The RPC has no top-level ORDER BY,
  // so card positions would otherwise reshuffle across date changes.
  const metrics = useMemo(
    () => [...rawMetrics].sort((a, b) => (a.setter_name || "").localeCompare(b.setter_name || "")),
    [rawMetrics]
  );

  // Proof modal: click metric row → open proof for that specific setter
  const handleMetricClick = useCallback(
    (
      metricType: ProofMetricType,
      setterId?: string,
      opts?: { defaultWindowMode?: "lifetime" | "window" }
    ) => {
      setProofMetric(metricType);
      setProofSetterId(setterId || null);
      setProofDefaultWindowMode(opts?.defaultWindowMode ?? "lifetime");
    },
    []
  );

  // Proof modal: click conversation in proof → open ConversationViewModal with scroll targets
  const handleProofConversationClick = useCallback(
    (
      conversationId: string,
      contactName: string,
      leadInstagram: string | null,
      highlightTimestamps?: { highlight: string; anchor?: string },
      nav?: import("../../hooks/data-fetching/useConversationPreviews").ConversationNav
    ) => {
      setSelectedConversation({
        id: conversationId,
        contactName,
        leadInstagram,
        highlightTimestamp: highlightTimestamps?.highlight,
        anchorTimestamp: highlightTimestamps?.anchor,
        siblings: nav?.siblings,
        index: nav?.index,
        hasMore: nav?.hasMore,
      });
    },
    []
  );

  // Dismiss a conversation from the ConversationViewModal — direct RPC, mirrors CardActions.
  // "dismissed" = reappears if the lead messages again (vs "dead"/Kill). With nav on (F6) the
  // reader stays open and advances, so drop the row from the live siblings snapshot first so the
  // advance lands on a valid neighbor; the dismiss filter on the proof RPCs hides it on refetch.
  const handleDismissConversation = useCallback(
    async (conversationId: string) => {
      setSelectedConversation((prev) =>
        prev && prev.siblings
          ? { ...prev, siblings: prev.siblings.filter((s) => s.conversationId !== conversationId) }
          : prev
      );
      if (!supabase) return;
      await supabase.rpc("bulk_dismiss_conversations", {
        p_conversation_ids: [conversationId],
        p_dismiss_type: "dismissed",
      });
      refreshLeaderboard();
    },
    [refreshLeaderboard]
  );

  if (!currentOrganization) {
    return <div className="p-8 text-center text-glass-muted">Please select an organization.</div>;
  }

  return (
    <div className="space-y-6">
      {/* Error state */}
      {error && (
        <div className="bg-ds-red/10 border border-ds-red/30 rounded-lg p-4 text-ds-red">
          <div className="flex items-center justify-between">
            <span>Error: {error}</span>
            <button
              onClick={refresh}
              className="anim-tap token-14 text-ds-red hover:text-ds-red underline"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Setter Scorecards — individual cards per setter */}
      <SetterScorecardGrid
        metrics={metrics}
        isLoading={isLoading || (isLoadingSetters && metrics.length === 0)}
        dayCount={dayCount}
        onMetricClick={handleMetricClick}
        organizationId={currentOrganization.id}
        setterIds={setterIds}
        dateRange={dateRange}
        timezone={currentOrganization?.timezone || "UTC"}
      />

      {/* Dismissed Leads — collapsible viewer at the very bottom. Reuses the
          ConversationViewModal below via the same setSelectedConversation path. */}
      <DismissedLeadsPanel
        organizationId={currentOrganization.id}
        setterIds={setterIds}
        dateRange={dateRange}
        timezone={currentOrganization?.timezone || "UTC"}
        setterNames={Object.fromEntries(metrics.map((m) => [m.setter_id, m.setter_name]))}
        onOpenConversation={(conv) =>
          setSelectedConversation({
            id: conv.id,
            contactName: conv.contactName,
            leadInstagram: conv.leadInstagram,
          })
        }
        total={leaderboard.total}
        byDismisser={leaderboard.byDismisser}
        byAssignee={leaderboard.byAssignee}
        leaderboardError={leaderboard.error}
        leaderboardLoading={leaderboard.isLoading}
        onViewGrid={() => setGridOpen(true)}
      />

      {/* Dismissed Leads — 3-col grid modal (S4). Owns its own rows; refreshes the
          leaderboard after any bulk dismiss/kill fired from inside it. */}
      <DismissedLeadsGridModal
        isOpen={gridOpen}
        onClose={() => setGridOpen(false)}
        organizationId={currentOrganization.id}
        timezone={currentOrganization?.timezone || "UTC"}
        dateRange={dateRange}
        total={leaderboard.total}
        onConversationClick={handleProofConversationClick}
        onAfterBulkDismiss={() => leaderboard.refresh()}
      />

      {/* Proof Modal — z-[9998] so ConversationViewModal (z-[9999]) stacks on top */}
      {proofMetric && (
        <SetterProofModal
          isOpen={proofMetric !== null}
          onClose={() => {
            setProofMetric(null);
            setProofSetterId(null);
          }}
          metricType={proofMetric}
          organizationId={currentOrganization.id}
          setterIds={proofSetterId ? [proofSetterId] : setterIds}
          dateRange={dateRange}
          timezone={currentOrganization?.timezone || "UTC"}
          onConversationClick={handleProofConversationClick}
          defaultWindowMode={proofDefaultWindowMode}
        />
      )}

      {/* Conversation View Modal — z-[9999], stacks above proof modal (z-[9998]) */}
      <ConversationViewModal
        isOpen={selectedConversation !== null}
        onClose={() => setSelectedConversation(null)}
        conversationId={selectedConversation?.id || null}
        contactName={selectedConversation?.contactName || "Unknown"}
        leadInstagram={selectedConversation?.leadInstagram}
        timezone={currentOrganization?.timezone || "UTC"}
        organizationId={currentOrganization?.id}
        highlightTimestamp={selectedConversation?.highlightTimestamp}
        anchorTimestamp={selectedConversation?.anchorTimestamp}
        onDismiss={handleDismissConversation}
        activeDateRange={
          dateRange.start && dateRange.end ? { start: dateRange.start, end: dateRange.end } : null
        }
        siblings={selectedConversation?.siblings}
        currentIndex={selectedConversation?.index}
        onNavigate={(nextIndex) =>
          setSelectedConversation((prev) => (prev ? { ...prev, index: nextIndex } : prev))
        }
        siblingsHasMore={selectedConversation?.hasMore}
        siblingsTotal={selectedConversation?.siblings?.length}
      />
    </div>
  );
};

export default SetterPerformanceTab;
